/**
 * Admin Audit Logs Page
 *
 * Displays activity logs for security auditing: who did what, where, and when.
 * Provides filtering by action type, resource type, date range, and search.
 */

import { useState, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Activity,
  Search,
  Filter,
  User,
  Link2,
  Folder,
  Key,
  Shield,
  Share2,
  Clock,
  ChevronDown,
  ChevronRight,
  ExternalLink,
} from 'lucide-react';
import { formatDate, cn } from '@/client/lib/utils';
import { apiFetch } from '@/client/lib/api';
import { Button } from '@/client/components/ui/button';
import { Card, CardContent } from '@/client/components/ui/card';
import { Input } from '@/client/components/ui/input';
import { Badge } from '@/client/components/ui/badge';
import { Skeleton } from '@/client/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/client/components/ui/select';

interface AuditLog {
  id: string;
  actor: {
    id: string | null;
    email: string | null;
    name: string | null;
    role: string | null;
  };
  action: string;
  actionLabel: string;
  resourceType: string;
  resourceId: string | null;
  resourceName: string | null;
  changes: {
    before?: Record<string, unknown>;
    after?: Record<string, unknown>;
  } | null;
  ipAddress: string | null;
  userAgent: string | null;
  createdAt: string;
}

const resourceTypeIcons: Record<string, typeof User> = {
  user: User,
  link: Link2,
  folder: Folder,
  token: Key,
  access_request: Shield,
  share: Share2,
};

const resourceTypeColors: Record<string, string> = {
  user: 'text-blue-500 bg-blue-500/10',
  link: 'text-amber-500 bg-amber-500/10',
  folder: 'text-purple-500 bg-purple-500/10',
  token: 'text-green-500 bg-green-500/10',
  access_request: 'text-orange-500 bg-orange-500/10',
  share: 'text-teal-500 bg-teal-500/10',
};

export function AdminAuditLogsPage() {
  const [page, setPage] = useState(1);
  const [resourceType, setResourceType] = useState<string>('all');
  const [search, setSearch] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Ref for virtualization scroll container
  const parentRef = useRef<HTMLDivElement>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'audit-logs', page, resourceType, search],
    queryFn: async () => {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50',
      });
      if (resourceType !== 'all') params.set('resourceType', resourceType);
      if (search) params.set('search', search);

      const response = await apiFetch(`/api/admin/audit-logs?${params}`);
      if (!response.ok) throw new Error('Failed to fetch audit logs');
      return response.json();
    },
  });

  const { data: stats } = useQuery({
    queryKey: ['admin', 'audit-logs', 'stats'],
    queryFn: async () => {
      const response = await apiFetch('/api/admin/audit-logs/stats');
      if (!response.ok) throw new Error('Failed to fetch stats');
      return response.json();
    },
  });

  const logs: AuditLog[] = data?.logs ?? [];
  const pagination = data?.pagination ?? { page: 1, totalPages: 1, total: 0 };

  // ⚡ Performance: Virtualize list for smooth scrolling with 50 items per page
  const rowVirtualizer = useVirtualizer({
    count: logs.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) => {
      // Dynamic size based on whether row is expanded
      const log = logs[index];
      return expandedId === log?.id ? 300 : 80; // Expanded ~300px, collapsed ~80px
    },
    overscan: 3, // Render 3 extra items above/below for smooth scrolling
  });

  return (
    <div className="space-y-6 stagger-children">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-3">
          <div className="size-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Activity className="size-5 text-primary" />
          </div>
          Audit Logs
        </h1>
        <p className="text-muted-foreground mt-1">
          Track who did what, where, and when in your application.
        </p>
      </div>

      {/* Stats Overview */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.byResourceType
            ?.slice(0, 4)
            .map((item: { resourceType: string; count: number }) => {
              const Icon = resourceTypeIcons[item.resourceType] || Activity;
              return (
                <Card key={item.resourceType} className="rounded-2xl py-0">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <div
                        className={cn(
                          'p-2 rounded-xl',
                          resourceTypeColors[item.resourceType] || 'bg-muted'
                        )}
                      >
                        <Icon className="size-4" />
                      </div>
                      <span className="text-sm font-medium capitalize">
                        {item.resourceType.replace('_', ' ')}
                      </span>
                    </div>
                    <p className="text-2xl font-bold tabular-nums">{item.count.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">events (30 days)</p>
                  </CardContent>
                </Card>
              );
            })}
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-4">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search by email or resource name…"
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            className="pl-10 rounded-xl"
          />
        </div>

        {/* Resource Type Filter */}
        <Select
          value={resourceType}
          onValueChange={(value) => {
            setResourceType(value);
            setPage(1);
          }}
        >
          <SelectTrigger className="w-[180px] rounded-xl">
            <Filter className="size-4 mr-2 text-muted-foreground" />
            <SelectValue placeholder="All Resources" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Resources</SelectItem>
            <SelectItem value="user">Users</SelectItem>
            <SelectItem value="link">Links</SelectItem>
            <SelectItem value="folder">Folders</SelectItem>
            <SelectItem value="token">API Tokens</SelectItem>
            <SelectItem value="access_request">Access Requests</SelectItem>
            <SelectItem value="share">Shares</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Logs Table */}
      <Card className="rounded-2xl overflow-hidden py-0">
        {isLoading ? (
          <CardContent className="p-4 space-y-3">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-16 rounded-xl" />
            ))}
          </CardContent>
        ) : logs.length === 0 ? (
          <CardContent className="p-12 text-center">
            <div className="size-16 rounded-2xl bg-muted/50 flex items-center justify-center mx-auto mb-4">
              <Activity className="size-8 text-muted-foreground/50" />
            </div>
            <h3 className="font-semibold mb-1">No audit logs found</h3>
            <p className="text-sm text-muted-foreground">
              Activity will appear here once actions are performed.
            </p>
          </CardContent>
        ) : (
          <div ref={parentRef} className="max-h-[70vh] overflow-y-auto">
            {/* Virtual list container */}
            <div
              style={{
                height: `${rowVirtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                const log = logs[virtualRow.index];
                if (!log) return null; // Guard against undefined
                const Icon = resourceTypeIcons[log.resourceType] || Activity;
                const isExpanded = expandedId === log.id;

                return (
                  <div
                    key={virtualRow.key}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: `${virtualRow.size}px`,
                      transform: `translateY(${virtualRow.start}px)`,
                    }}
                    className="border-b group"
                  >
                    <button
                      onClick={() => setExpandedId(isExpanded ? null : log.id)}
                      className="w-full p-4 flex items-start gap-4 hover:bg-muted/50 text-left transition-colors"
                    >
                      {/* Icon */}
                      <div
                        className={cn(
                          'p-2.5 rounded-xl shrink-0',
                          resourceTypeColors[log.resourceType] || 'bg-muted'
                        )}
                      >
                        <Icon className="size-4" />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold">{log.actionLabel}</span>
                          {log.resourceName && (
                            <>
                              <span className="text-muted-foreground">·</span>
                              <span className="text-muted-foreground truncate max-w-[200px]">
                                {log.resourceName}
                              </span>
                            </>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                          <span className="flex items-center gap-1">
                            <User className="size-3" />
                            {log.actor.email ||
                              (log.action.startsWith('public.') ? 'Anonymous' : 'System')}
                          </span>
                          {log.actor.role === 'admin' && (
                            <Badge variant="primary-subtle" className="text-xs">
                              Admin
                            </Badge>
                          )}
                          {log.action.startsWith('public.') && !log.actor.email && (
                            <Badge variant="secondary" className="text-xs">
                              Public
                            </Badge>
                          )}
                          {log.action.startsWith('api.') && (
                            <Badge variant="secondary" className="text-xs">
                              API
                            </Badge>
                          )}
                        </div>
                      </div>

                      {/* Timestamp */}
                      <div className="text-sm text-muted-foreground flex items-center gap-1 shrink-0">
                        <Clock className="size-3" />
                        {formatDate(log.createdAt)}
                      </div>

                      {/* Expand indicator */}
                      <div className="text-muted-foreground group-hover:text-foreground transition-colors">
                        {isExpanded ? (
                          <ChevronDown className="size-4" />
                        ) : (
                          <ChevronRight className="size-4" />
                        )}
                      </div>
                    </button>

                    {/* Expanded details */}
                    {isExpanded && (
                      <div className="px-4 pb-4 ml-14 space-y-3 animate-in fade-in-0 slide-in-from-top-2">
                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div className="p-3 rounded-xl bg-muted/50">
                            <span className="text-muted-foreground text-xs">Action</span>
                            <code className="block mt-1 text-xs font-mono">{log.action}</code>
                          </div>
                          <div className="p-3 rounded-xl bg-muted/50">
                            <span className="text-muted-foreground text-xs">Resource Type</span>
                            <span className="block mt-1 capitalize font-medium">
                              {log.resourceType.replace('_', ' ')}
                            </span>
                          </div>
                          {log.resourceId && (
                            <div className="p-3 rounded-xl bg-muted/50">
                              <span className="text-muted-foreground text-xs">Resource ID</span>
                              <code className="block mt-1 text-xs font-mono truncate">
                                {log.resourceId}
                              </code>
                            </div>
                          )}
                          {log.ipAddress && (
                            <div className="p-3 rounded-xl bg-muted/50">
                              <span className="text-muted-foreground text-xs">IP Address</span>
                              <span className="block mt-1 font-medium">{log.ipAddress}</span>
                            </div>
                          )}
                        </div>

                        {/* Changes */}
                        {log.changes &&
                          (Object.keys(log.changes.before || {}).length > 0 ||
                            Object.keys(log.changes.after || {}).length > 0) && (
                            <div className="mt-3">
                              <span className="text-sm font-medium text-muted-foreground">
                                Changes:
                              </span>
                              <div className="mt-2 grid grid-cols-2 gap-4">
                                {log.changes.before &&
                                  Object.keys(log.changes.before).length > 0 && (
                                    <div className="p-3 rounded-xl bg-destructive/5 border border-destructive/20">
                                      <Badge variant="destructive" className="text-xs mb-2">
                                        Before
                                      </Badge>
                                      <pre className="text-xs overflow-x-auto font-mono">
                                        {JSON.stringify(log.changes.before, null, 2)}
                                      </pre>
                                    </div>
                                  )}
                                {log.changes.after && Object.keys(log.changes.after).length > 0 && (
                                  <div className="p-3 rounded-xl bg-success/5 border border-success/20">
                                    <Badge variant="success" className="text-xs mb-2">
                                      After
                                    </Badge>
                                    <pre className="text-xs overflow-x-auto font-mono">
                                      {JSON.stringify(log.changes.after, null, 2)}
                                    </pre>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                        {/* User Agent */}
                        {log.userAgent && (
                          <div className="text-xs text-muted-foreground truncate p-3 rounded-xl bg-muted/30">
                            <span className="font-medium">User Agent:</span> {log.userAgent}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-muted-foreground">
            Showing page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
          </p>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="rounded-xl"
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
              disabled={page === pagination.totalPages}
              className="rounded-xl"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* External service note */}
      <Card className="rounded-2xl border-dashed bg-muted/30 py-0">
        <CardContent className="p-4 flex items-start gap-3">
          <div className="size-10 rounded-xl bg-muted flex items-center justify-center shrink-0">
            <ExternalLink className="size-5 text-muted-foreground" />
          </div>
          <div>
            <p className="font-medium">Full audit trail</p>
            <p className="text-sm text-muted-foreground">
              For long-term retention and advanced queries, consider shipping logs to an external
              service like Axiom, Datadog, or CloudWatch.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
